(() => {
  const dataCache = window.MoebelloftUpholsteryDataCache || new Map();
  window.MoebelloftUpholsteryDataCache = dataCache;

  const typeQuery = `
    query UpholsteryTypes($after: String) {
      metaobjects(type: "materialart", first: 250, after: $after) {
        nodes {
          id
          handle
          label: field(key: "label") { value }
        }
        pageInfo { hasNextPage endCursor }
      }
    }
  `;

  const groupQuery = `
    query UpholsteryGroups($after: String) {
      metaobjects(type: "materialgruppe", first: 250, after: $after) {
        nodes {
          id
          handle
          name: field(key: "name") { value }
        }
        pageInfo { hasNextPage endCursor }
      }
    }
  `;

  const materialQuery = `
    query UpholsteryMaterials($after: String) {
      metaobjects(type: "material", first: 250, after: $after) {
        nodes {
          id
          handle
          label: field(key: "label") { value }
          art: field(key: "materialart") {
            reference { ... on Metaobject { id } }
          }
          group: field(key: "materialgruppe") {
            reference { ... on Metaobject { id } }
          }
          image: field(key: "vorschaubild") {
            reference {
              ... on MediaImage {
                image {
                  url(transform: { maxWidth: 480, maxHeight: 480 })
                  altText
                  width
                  height
                }
              }
            }
          }
        }
        pageInfo { hasNextPage endCursor }
      }
    }
  `;

  const normalize = (value) => String(value || '').trim().toLocaleLowerCase('de');

  const postQuery = async (endpoint, token, query, after) => {
    const response = await fetch(endpoint, {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        'Content-Type': 'application/json',
        'X-Shopify-Storefront-Access-Token': token,
      },
      body: JSON.stringify({ query, variables: { after } }),
    });

    if (!response.ok) {
      throw new Error(`Storefront API ${response.status}`);
    }

    const payload = await response.json();
    if (payload.errors?.length || !payload.data?.metaobjects) {
      throw new Error(payload.errors?.[0]?.message || 'Keine Metaobjekt-Daten erhalten.');
    }

    return payload.data.metaobjects;
  };

  const fetchAll = async (endpoint, token, query) => {
    const nodes = [];
    let after = null;
    let hasNextPage = false;

    do {
      const connection = await postQuery(endpoint, token, query, after);
      nodes.push(...connection.nodes);
      hasNextPage = connection.pageInfo.hasNextPage;
      after = connection.pageInfo.endCursor;
    } while (hasNextPage);

    return nodes;
  };

  const prepareData = async (endpoint, token) => {
    const [typeNodes, groupNodes, materialNodes] = await Promise.all([
      fetchAll(endpoint, token, typeQuery),
      fetchAll(endpoint, token, groupQuery),
      fetchAll(endpoint, token, materialQuery),
    ]);

    const typeOrder = new Map([
      ['stoff', 0],
      ['hybridleder', 1],
      ['leder', 2],
    ]);

    const typesById = new Map();
    typeNodes.forEach((node) => {
      const label = node.label?.value?.trim();
      if (!node.id || !label) return;

      typesById.set(node.id, {
        id: node.id,
        handle: node.handle,
        label,
        key: normalize(label),
        groups: new Map(),
      });
    });

    const groupsById = new Map();
    groupNodes.forEach((node) => {
      const name = node.name?.value?.trim();
      if (!node.id || !name) return;

      groupsById.set(node.id, {
        id: node.id,
        handle: node.handle,
        name,
      });
    });

    materialNodes.forEach((node) => {
      const label = node.label?.value?.trim();
      const typeId = node.art?.reference?.id;
      const groupId = node.group?.reference?.id;
      const image = node.image?.reference?.image;
      const type = typesById.get(typeId);
      const group = groupsById.get(groupId);

      if (!node.id || !label || !image?.url || !type || !group) return;

      if (!type.groups.has(groupId)) {
        type.groups.set(groupId, { ...group, materials: [] });
      }

      type.groups.get(groupId).materials.push({
        id: node.id,
        handle: node.handle,
        label,
        image,
      });
    });

    return Array.from(typesById.values())
      .map((type) => ({
        ...type,
        groups: Array.from(type.groups.values())
          .filter((group) => group.materials.length > 0)
          .sort((a, b) => a.name.localeCompare(b.name, 'de', { sensitivity: 'base' }))
          .map((group) => ({
            ...group,
            materials: group.materials.sort((a, b) =>
              a.label.localeCompare(b.label, 'de', { numeric: true, sensitivity: 'base' })
            ),
          })),
      }))
      .filter((type) => type.groups.length > 0)
      .sort((a, b) => {
        const aOrder = typeOrder.has(a.key) ? typeOrder.get(a.key) : 99;
        const bOrder = typeOrder.has(b.key) ? typeOrder.get(b.key) : 99;
        return aOrder - bOrder || a.label.localeCompare(b.label, 'de');
      });
  };

  const getData = (endpoint, token, force = false) => {
    const cacheKey = `${endpoint}:${token}`;
    if (force) dataCache.delete(cacheKey);

    if (!dataCache.has(cacheKey)) {
      const request = prepareData(endpoint, token).catch((error) => {
        dataCache.delete(cacheKey);
        throw error;
      });
      dataCache.set(cacheKey, request);
    }

    return dataCache.get(cacheKey);
  };

  class UpholsteryDrawer extends HTMLElement {
    connectedCallback() {
      if (this.isInitialized) return;
      this.isInitialized = true;

      this.endpoint = this.dataset.apiEndpoint;
      this.token = this.dataset.storefrontToken;
      this.defaultType = normalize(this.dataset.defaultType || 'stoff');
      this.openButton = this.querySelector('[data-upholstery-open]');
      this.drawer = this.querySelector('[data-upholstery-drawer]');
      this.panel = this.querySelector('[data-upholstery-panel]');
      this.closeButtons = this.querySelectorAll('[data-upholstery-close]');
      this.tabs = this.querySelector('[data-upholstery-tabs]');
      this.body = this.querySelector('[data-upholstery-body]');
      this.loading = this.querySelector('[data-upholstery-loading]');
      this.error = this.querySelector('[data-upholstery-error]');
      this.errorText = this.querySelector('[data-upholstery-error-text]');
      this.retryButton = this.querySelector('[data-upholstery-retry]');
      this.empty = this.querySelector('[data-upholstery-empty]');
      this.content = this.querySelector('[data-upholstery-content]');
      this.closeButton = this.querySelector('.upholstery-drawer__close');

      if (this.drawer && this.drawer.parentElement !== document.body) {
        document.body.appendChild(this.drawer);
      }

      this.handleOpen = this.open.bind(this);
      this.handleClose = this.close.bind(this);
      this.handleKeydown = this.onKeydown.bind(this);
      this.handleRetry = () => this.loadMaterials(true);
      this.handleOtherDrawer = (event) => {
        if (event.detail !== this && this.classList.contains('is-open')) this.close();
      };

      this.openButton?.addEventListener('click', this.handleOpen);
      this.closeButtons.forEach((button) => button.addEventListener('click', this.handleClose));
      this.retryButton?.addEventListener('click', this.handleRetry);
      document.addEventListener('keydown', this.handleKeydown);
      document.addEventListener('moebelloft:upholstery-drawer-open', this.handleOtherDrawer);
    }

    disconnectedCallback() {
      this.openButton?.removeEventListener('click', this.handleOpen);
      this.closeButtons?.forEach((button) => button.removeEventListener('click', this.handleClose));
      this.retryButton?.removeEventListener('click', this.handleRetry);
      document.removeEventListener('keydown', this.handleKeydown);
      document.removeEventListener('moebelloft:upholstery-drawer-open', this.handleOtherDrawer);
      window.clearTimeout(this.focusTimer);

      if (this.classList.contains('is-open')) {
        if (typeof removeTrapFocus === 'function') removeTrapFocus();
        document.body.classList.remove('ml-upholstery-drawer-open');
      }

      this.classList.remove('is-open');
      this.drawer?.classList.remove('is-open');
      this.drawer?.setAttribute('aria-hidden', 'true');
      if (this.panel) this.panel.inert = true;
      this.openButton?.setAttribute('aria-expanded', 'false');

      if (this.drawer && this.drawer.parentElement === document.body) {
        this.appendChild(this.drawer);
      }

      this.isInitialized = false;
    }

    open() {
      if (!this.drawer || !this.panel) return;

      document.dispatchEvent(new CustomEvent('moebelloft:upholstery-drawer-open', { detail: this }));
      this.activeTrigger = this.openButton;
      this.classList.add('is-open');
      this.drawer.classList.add('is-open');
      this.drawer.setAttribute('aria-hidden', 'false');
      this.panel.inert = false;
      this.openButton?.setAttribute('aria-expanded', 'true');
      document.body.classList.add('ml-upholstery-drawer-open');

      const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      window.clearTimeout(this.focusTimer);
      this.focusTimer = window.setTimeout(() => {
        if (typeof trapFocus === 'function') {
          trapFocus(this.panel, this.closeButton || this.panel);
        } else {
          this.closeButton?.focus();
        }
      }, reducedMotion ? 0 : 290);

      if (!this.hasLoaded) this.loadMaterials();
    }

    close() {
      if (!this.classList.contains('is-open')) return;

      window.clearTimeout(this.focusTimer);
      this.classList.remove('is-open');
      this.drawer.classList.remove('is-open');
      this.drawer.setAttribute('aria-hidden', 'true');
      this.panel.inert = true;
      this.openButton?.setAttribute('aria-expanded', 'false');

      if (typeof removeTrapFocus === 'function') {
        removeTrapFocus(this.activeTrigger);
      } else {
        this.activeTrigger?.focus();
      }

      if (!document.querySelector('upholstery-drawer.is-open')) {
        document.body.classList.remove('ml-upholstery-drawer-open');
      }
    }

    onKeydown(event) {
      if (event.key === 'Escape' && this.classList.contains('is-open')) {
        this.close();
        return;
      }

      if (!event.target.matches?.('[role="tab"]')) return;
      const tabs = Array.from(this.tabs.querySelectorAll('[role="tab"]'));
      const currentIndex = tabs.indexOf(event.target);
      if (currentIndex < 0) return;

      let nextIndex = currentIndex;
      if (event.key === 'ArrowRight') nextIndex = (currentIndex + 1) % tabs.length;
      if (event.key === 'ArrowLeft') nextIndex = (currentIndex - 1 + tabs.length) % tabs.length;
      if (event.key === 'Home') nextIndex = 0;
      if (event.key === 'End') nextIndex = tabs.length - 1;
      if (nextIndex === currentIndex) return;

      event.preventDefault();
      tabs[nextIndex].focus();
      this.activateType(tabs[nextIndex].dataset.typeKey);
    }

    setState(state) {
      this.body.setAttribute('aria-busy', state === 'loading' ? 'true' : 'false');
      this.loading.hidden = state !== 'loading';
      this.error.hidden = state !== 'error';
      this.empty.hidden = state !== 'empty';
      this.content.hidden = state !== 'content';
      if (state !== 'content') this.tabs.hidden = true;
    }

    async loadMaterials(force = false) {
      this.setState('loading');

      if (!this.endpoint || !this.token) {
        this.showError('Die Bezugsübersicht ist noch nicht vollständig eingerichtet.');
        return;
      }

      try {
        const types = await getData(this.endpoint, this.token, force);
        if (!types.length) {
          this.setState('empty');
          this.hasLoaded = true;
          return;
        }

        this.render(types);
        this.setState('content');
        this.tabs.hidden = false;
        this.hasLoaded = true;
      } catch (error) {
        this.showError('Die Bezüge konnten nicht geladen werden. Bitte versuchen Sie es erneut.');
      }
    }

    showError(message) {
      this.errorText.textContent = message;
      this.setState('error');
    }

    render(types) {
      this.tabs.replaceChildren();
      this.content.replaceChildren();

      types.forEach((type, typeIndex) => {
        const tabId = `${this.drawer.id}-Tab-${typeIndex}`;
        const panelId = `${this.drawer.id}-Panel-${typeIndex}`;
        const tab = document.createElement('button');
        tab.type = 'button';
        tab.id = tabId;
        tab.className = 'upholstery-drawer__tab focus-inset';
        tab.dataset.typeKey = type.key;
        tab.setAttribute('role', 'tab');
        tab.setAttribute('aria-controls', panelId);
        tab.setAttribute('aria-selected', 'false');
        tab.tabIndex = -1;
        tab.textContent = type.label;
        tab.addEventListener('click', () => this.activateType(type.key));
        this.tabs.append(tab);

        const panel = document.createElement('div');
        panel.id = panelId;
        panel.className = 'upholstery-drawer__type-panel';
        panel.dataset.typePanel = type.key;
        panel.setAttribute('role', 'tabpanel');
        panel.setAttribute('aria-labelledby', tabId);
        panel.tabIndex = 0;
        panel.hidden = true;

        type.groups.forEach((group) => panel.append(this.createGroup(group)));
        this.content.append(panel);
      });

      const requestedType = types.find((type) => type.key === this.defaultType);
      this.activateType((requestedType || types[0]).key, false);
    }

    createGroup(group) {
      const groupSection = document.createElement('section');
      groupSection.className = 'upholstery-drawer__group';

      const heading = document.createElement('h3');
      heading.className = 'upholstery-drawer__group-title ml-type-ui-title';
      heading.textContent = group.name;
      groupSection.append(heading);

      const grid = document.createElement('div');
      grid.className = 'upholstery-drawer__grid';

      group.materials.forEach((material) => {
        const card = document.createElement('figure');
        card.className = 'upholstery-drawer__card';
        card.dataset.materialId = material.id;
        card.dataset.materialHandle = material.handle;

        const media = document.createElement('div');
        media.className = 'upholstery-drawer__card-media';

        const image = document.createElement('img');
        image.className = 'upholstery-drawer__card-image';
        image.src = material.image.url;
        image.alt = material.image.altText || `${group.name} ${material.label}`;
        image.width = 480;
        image.height = 480;
        image.loading = 'lazy';
        image.decoding = 'async';
        media.append(image);

        const caption = document.createElement('figcaption');
        caption.className = 'upholstery-drawer__card-name ml-type-small';
        caption.textContent = material.label;

        card.append(media, caption);
        grid.append(card);
      });

      groupSection.append(grid);
      return groupSection;
    }

    activateType(typeKey, moveScroll = true) {
      this.tabs.querySelectorAll('[role="tab"]').forEach((tab) => {
        const isActive = tab.dataset.typeKey === typeKey;
        tab.setAttribute('aria-selected', String(isActive));
        tab.tabIndex = isActive ? 0 : -1;
      });

      this.content.querySelectorAll('[role="tabpanel"]').forEach((panel) => {
        panel.hidden = panel.dataset.typePanel !== typeKey;
      });

      if (moveScroll) this.body.scrollTo({ top: 0, behavior: 'auto' });
    }
  }

  if (!customElements.get('upholstery-drawer')) {
    customElements.define('upholstery-drawer', UpholsteryDrawer);
  }
})();
